import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

// ─── Constants ───────────────────────────────────────────────────────────────

const COLS       = 20;
const ROWS       = 20;
const CANVAS_PX  = 400;          // logical canvas resolution
const CELL       = CANVAS_PX / COLS;
const BASE_SPEED = 140;          // ms per tick
const MIN_SPEED  = 65;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function onSnake(snake, x, y) {
  return snake.some(s => s.x === x && s.y === y);
}

function rndFood(snake) {
  let x, y;
  do {
    x = Math.floor(Math.random() * COLS);
    y = Math.floor(Math.random() * ROWS);
  } while (onSnake(snake, x, y));
  return { x, y };
}

function makeInitState() {
  const snake = [{ x: 10, y: 10 }, { x: 9, y: 10 }, { x: 8, y: 10 }];
  return {
    snake,
    food:    rndFood(snake),
    dir:     { x: 1, y: 0 },
    nextDir: { x: 1, y: 0 },
    score:   0,
    speed:   BASE_SPEED,
    over:    false,
  };
}

const DIR_KEYS = {
  ArrowUp:    { x: 0,  y: -1 },
  ArrowDown:  { x: 0,  y:  1 },
  ArrowLeft:  { x: -1, y:  0 },
  ArrowRight: { x: 1,  y:  0 },
  w:          { x: 0,  y: -1 },
  s:          { x: 0,  y:  1 },
  a:          { x: -1, y:  0 },
  d:          { x: 1,  y:  0 },
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const PageStyles = styled.div`
  padding: 16rem 0 10rem;
  min-height: 80vh;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 1.4rem;
  color: var(--highlight-blue);
  opacity: 0.7;
  margin-bottom: 3rem;
  transition: opacity 0.2s ease;
  &:hover { opacity: 1; }
  &::before { content: '←'; }
`;

const GameWrapper = styled.div`
  max-width: 520px;
  margin: 0 auto;
  padding: 0 2rem;
  user-select: none;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.6rem;
  flex-wrap: wrap;
  gap: 1rem;

  .game__title {
    font-size: 3.6rem;
    font-family: 'Montserrat Bold';
    color: var(--gray-1);
    line-height: 1;
  }
`;

const ScoreGroup = styled.div`
  display: flex;
  gap: 1rem;
`;

const ScoreBox = styled.div`
  background: var(--deep-dark);
  border: 1px solid var(--gray-2);
  border-radius: 8px;
  padding: 0.6rem 1.4rem;
  text-align: center;
  min-width: 80px;

  .label {
    font-size: 1rem;
    text-transform: uppercase;
    letter-spacing: 0.1rem;
    color: var(--gray-1);
    opacity: 0.45;
  }
  .value {
    font-size: 2rem;
    font-family: 'Montserrat Bold';
    color: var(--highlight-blue);
    line-height: 1.2;
  }
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.2rem;
  gap: 1rem;

  .hint {
    font-size: 1.2rem;
    color: var(--gray-1);
    opacity: 0.35;
  }
`;

const NewGameBtn = styled.button`
  font-size: 1.3rem;
  padding: 0.6em 1.8em;
  background: transparent;
  color: var(--highlight-blue);
  border: 2px solid var(--highlight-blue);
  border-radius: 8px;
  cursor: pointer;
  font-family: 'RobotoMono Regular';
  transition: background 0.2s ease, color 0.2s ease;
  white-space: nowrap;
  &:hover { background: var(--highlight-blue); color: var(--dark-bg); }
`;

const BoardWrapper = styled.div`
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid var(--gray-2);
  line-height: 0; /* kill inline gap under canvas */

  canvas {
    width: 100%;
    height: auto;
    display: block;
  }
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.2rem;
  z-index: 10;

  h2 {
    font-size: 3rem;
    font-family: 'Montserrat Bold';
    color: var(--gray-1);
  }
  p {
    font-size: 1.4rem;
    color: var(--gray-1);
    opacity: 0.55;
    text-align: center;
    line-height: 1.6;
    padding: 0 2rem;
  }
`;

const OBtn = styled.button`
  font-size: 1.5rem;
  padding: 0.75em 2.4em;
  border-radius: 8px;
  cursor: pointer;
  font-family: 'RobotoMono Regular';
  background: var(--highlight-blue);
  color: var(--dark-bg);
  border: none;
  transition: opacity 0.2s ease;
  &:hover { opacity: 0.85; }
`;

const SpeedBadge = styled.div`
  margin-top: 1rem;
  text-align: right;
  font-size: 1.1rem;
  color: var(--gray-1);
  opacity: 0.3;
`;

// ─── Component ───────────────────────────────────────────────────────────────

export default function SnakeGame() {
  const canvasRef  = useRef(null);
  const stateRef   = useRef(null);
  const loopRef    = useRef(null);
  const tickFn     = useRef(null);
  const touchStart = useRef(null);

  const [score, setScore]   = useState(0);
  const [best,  setBest]    = useState(() => Number(localStorage.getItem('snake-best') || 0));
  const [phase, setPhase]   = useState('idle'); // 'idle' | 'playing' | 'over'

  // ── Draw ────────────────────────────────────────────────────────────────────
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const cs  = getComputedStyle(document.documentElement);
    const hl  = cs.getPropertyValue('--highlight-blue').trim() || '#4a9eff';
    const bg  = cs.getPropertyValue('--deep-dark').trim()      || '#1e1e1e';
    const g2  = cs.getPropertyValue('--gray-2').trim()         || '#363636';

    // Background
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, CANVAS_PX, CANVAS_PX);

    // Subtle grid dots
    ctx.fillStyle = g2;
    ctx.globalAlpha = 0.6;
    for (let x = 0; x < COLS; x++) {
      for (let y = 0; y < ROWS; y++) {
        ctx.fillRect(x * CELL + CELL / 2 - 0.75, y * CELL + CELL / 2 - 0.75, 1.5, 1.5);
      }
    }
    ctx.globalAlpha = 1;

    const s = stateRef.current;
    if (!s) return;

    // Food — pulsing circle
    ctx.fillStyle = '#f65e3b';
    ctx.beginPath();
    ctx.arc(
      s.food.x * CELL + CELL / 2,
      s.food.y * CELL + CELL / 2,
      CELL / 2 - 3, 0, Math.PI * 2
    );
    ctx.fill();

    // Snake segments — head brightest, body fades
    const pad = 2;
    s.snake.forEach((seg, i) => {
      const alpha = i === 0 ? 1 : Math.max(0.3, 1 - (i / s.snake.length) * 0.65);
      ctx.globalAlpha = alpha;
      ctx.fillStyle   = i === 0 ? hl : hl;
      ctx.fillRect(
        seg.x * CELL + pad,
        seg.y * CELL + pad,
        CELL - pad * 2,
        CELL - pad * 2
      );
    });
    ctx.globalAlpha = 1;

    // Head eyes
    const head = s.snake[0];
    const hx = head.x * CELL;
    const hy = head.y * CELL;
    ctx.fillStyle = bg;
    const eyeR = 2;
    const { x: dx, y: dy } = s.dir;
    // Place eyes perpendicular to direction
    const ex1 = hx + CELL / 2 + dy * 4 - dx * 2;
    const ey1 = hy + CELL / 2 - dx * 4 - dy * 2;
    const ex2 = hx + CELL / 2 - dy * 4 - dx * 2;
    const ey2 = hy + CELL / 2 + dx * 4 - dy * 2;
    ctx.beginPath(); ctx.arc(ex1, ey1, eyeR, 0, Math.PI * 2); ctx.fill();
    ctx.beginPath(); ctx.arc(ex2, ey2, eyeR, 0, Math.PI * 2); ctx.fill();
  }, []);

  // ── Game tick (stored in ref so interval always calls latest version) ───────
  useEffect(() => {
    tickFn.current = () => {
      const s = stateRef.current;
      if (!s || s.over) return;

      s.dir = s.nextDir;
      const h  = s.snake[0];
      const nh = { x: h.x + s.dir.x, y: h.y + s.dir.y };

      // Wall or self collision
      const hitWall = nh.x < 0 || nh.x >= COLS || nh.y < 0 || nh.y >= ROWS;
      const hitSelf = s.snake.some(seg => seg.x === nh.x && seg.y === nh.y);
      if (hitWall || hitSelf) {
        s.over = true;
        clearInterval(loopRef.current);
        draw();
        setPhase('over');
        return;
      }

      const ate = nh.x === s.food.x && nh.y === s.food.y;
      s.snake = [nh, ...s.snake];
      if (!ate) s.snake.pop();

      if (ate) {
        s.food   = rndFood(s.snake);
        s.score += 10;
        setScore(s.score);
        setBest(b => {
          const nb = Math.max(b, s.score);
          if (nb > b) localStorage.setItem('snake-best', String(nb));
          return nb;
        });
        // Speed up every 5 food eaten
        const eaten    = s.score / 10;
        const newSpeed = Math.max(MIN_SPEED, BASE_SPEED - Math.floor(eaten / 5) * 10);
        if (newSpeed !== s.speed) {
          s.speed = newSpeed;
          clearInterval(loopRef.current);
          loopRef.current = setInterval(() => tickFn.current?.(), newSpeed);
        }
      }

      draw();
    };
  }); // runs every render so tickFn always has latest closure

  // ── Start / Restart ─────────────────────────────────────────────────────────
  const startGame = useCallback(() => {
    clearInterval(loopRef.current);
    stateRef.current = makeInitState();
    setScore(0);
    setPhase('playing');
    draw();
    loopRef.current = setInterval(() => tickFn.current?.(), BASE_SPEED);
  }, [draw]);

  // Cleanup on unmount
  useEffect(() => () => clearInterval(loopRef.current), []);

  // Initial canvas draw
  useEffect(() => { draw(); }, [draw]);

  // ── Keyboard ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        if (phase !== 'playing') { startGame(); return; }
      }
      const d = DIR_KEYS[e.key] ?? DIR_KEYS[e.key.toLowerCase()];
      if (!d) return;
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) e.preventDefault();
      const s = stateRef.current;
      if (!s || s.over) return;
      // Block 180° reversal
      if (d.x !== 0 && s.dir.x !== 0) return;
      if (d.y !== 0 && s.dir.y !== 0) return;
      s.nextDir = d;
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase, startGame]);

  // ── Touch / Swipe ───────────────────────────────────────────────────────────
  const onTouchStart = (e) => {
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  };

  const onTouchEnd = (e) => {
    if (!touchStart.current) return;
    const dx = e.changedTouches[0].clientX - touchStart.current.x;
    const dy = e.changedTouches[0].clientY - touchStart.current.y;
    touchStart.current = null;
    if (Math.abs(dx) < 20 && Math.abs(dy) < 20) return;

    if (phase !== 'playing') { startGame(); return; }
    const s = stateRef.current;
    if (!s || s.over) return;

    let d;
    if (Math.abs(dx) > Math.abs(dy)) {
      d = dx > 0 ? { x: 1, y: 0 } : { x: -1, y: 0 };
      if (s.dir.x !== 0) return;
    } else {
      d = dy > 0 ? { x: 0, y: 1 } : { x: 0, y: -1 };
      if (s.dir.y !== 0) return;
    }
    s.nextDir = d;
  };

  // Speed label
  const speedLabel = stateRef.current
    ? stateRef.current.speed <= 80  ? 'Fast'
    : stateRef.current.speed <= 110 ? 'Normal'
    : 'Slow'
    : '';

  return (
    <PageStyles>
      <Helmet><title>Snake | Anas Qazi</title></Helmet>
      <div className="container">
        <BackLink to="/games">All Games</BackLink>
      </div>

      <GameWrapper>
        <TopBar>
          <span className="game__title">🐍 Snake</span>
          <ScoreGroup>
            <ScoreBox>
              <div className="label">Score</div>
              <div className="value">{score}</div>
            </ScoreBox>
            <ScoreBox>
              <div className="label">Best</div>
              <div className="value">{best}</div>
            </ScoreBox>
          </ScoreGroup>
        </TopBar>

        <Controls>
          <span className="hint">Arrow keys / WASD · Swipe on mobile</span>
          <NewGameBtn onClick={startGame}>New Game</NewGameBtn>
        </Controls>

        <BoardWrapper
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <canvas ref={canvasRef} width={CANVAS_PX} height={CANVAS_PX} />

          {phase === 'idle' && (
            <Overlay>
              <h2>🐍 Snake</h2>
              <p>
                Eat the food, grow your snake.<br />
                Don't hit the walls or yourself.
              </p>
              <OBtn onClick={startGame}>Start Game</OBtn>
            </Overlay>
          )}

          {phase === 'over' && (
            <Overlay>
              <h2>Game Over</h2>
              <p>Score: {score}</p>
              <OBtn onClick={startGame}>Play Again</OBtn>
            </Overlay>
          )}
        </BoardWrapper>

        {phase === 'playing' && speedLabel && (
          <SpeedBadge>Speed: {speedLabel}</SpeedBadge>
        )}
      </GameWrapper>
    </PageStyles>
  );
}
